import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistance, formatRelative, isValid, parseISO } from 'date-fns';
import { fr, ar, enUS } from 'date-fns/locale';

// ============================================
// CLASS NAME UTILITIES
// ============================================

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ============================================
// DATE UTILITIES
// ============================================

const locales = {
  fr,
  ar,
  en: enUS,
};

export function formatDate(
  date: string | Date | undefined | null,
  formatStr: string = 'dd/MM/yyyy',
  locale: 'fr' | 'ar' | 'en' = 'fr'
): string {
  if (!date) return '-';
  
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  if (!isValid(dateObj)) return '-';
  
  return format(dateObj, formatStr, { locale: locales[locale] });
}

export function formatDateTime(
  date: string | Date | undefined | null,
  locale: 'fr' | 'ar' | 'en' = 'fr'
): string {
  return formatDate(date, 'dd/MM/yyyy HH:mm', locale);
}

export function formatDateRelative(
  date: string | Date | undefined | null,
  locale: 'fr' | 'ar' | 'en' = 'fr'
): string {
  if (!date) return '-';
  
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  if (!isValid(dateObj)) return '-';
  
  return formatRelative(dateObj, new Date(), { locale: locales[locale] });
}

export function formatDateDistance(
  date: string | Date | undefined | null,
  locale: 'fr' | 'ar' | 'en' = 'fr'
): string {
  if (!date) return '-';
  
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  if (!isValid(dateObj)) return '-';
  
  return formatDistance(dateObj, new Date(), { addSuffix: true, locale: locales[locale] });
}

// ============================================
// NUMBER & CURRENCY UTILITIES
// ============================================

export function formatNumber(
  value: number | undefined | null,
  options: Intl.NumberFormatOptions = {}
): string {
  if (value === undefined || value === null) return '-';
  
  return new Intl.NumberFormat('fr-TN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
    ...options,
  }).format(value);
}

export function formatCurrency(
  value: number | undefined | null,
  currency: string = 'TND',
  locale: string = 'fr-TN'
): string {
  if (value === undefined || value === null) return '-';
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(value);
}

export function formatPercent(
  value: number | undefined | null,
  decimals: number = 2
): string {
  if (value === undefined || value === null) return '-';
  
  return `${value.toFixed(decimals)}%`;
}

export function parseNumber(value: string): number {
  // Handle French number format (comma as decimal separator)
  const normalized = value.replace(/\s/g, '').replace(',', '.');
  return parseFloat(normalized) || 0;
}

// ============================================
// STRING UTILITIES
// ============================================

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function titleCase(str: string): string {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

// ============================================
// VALIDATION UTILITIES
// ============================================

// Tunisia Matricule Fiscal format: 1234567/A/A/M/000
export function isValidMatriculeFiscal(value: string): boolean {
  const pattern = /^\d{7}\/[A-Z]\/[A-Z]\/[A-Z]\/\d{3}$/;
  return pattern.test(value);
}

// Tunisia phone number
export function isValidTunisiaPhone(value: string): boolean {
  const pattern = /^(\+216)?[2-9]\d{7}$/;
  return pattern.test(value.replace(/\s/g, ''));
}

// Tunisia CIN
export function isValidCIN(value: string): boolean {
  const pattern = /^\d{8}$/;
  return pattern.test(value.replace(/\s/g, ''));
}

// IBAN validation (basic)
export function isValidIBAN(value: string): boolean {
  const iban = value.replace(/\s/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{4,30}$/.test(iban)) return false;
  
  // Tunisia IBAN: TN59 + 20 digits
  if (iban.startsWith('TN') && iban.length !== 24) return false;
  
  return true;
}

export function isValidEmail(value: string): boolean {
  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return pattern.test(value);
}

// ============================================
// FILE UTILITIES
// ============================================

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export function getFileExtension(filename: string): string {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
}

export function getMimeTypeIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.includes('pdf')) return 'pdf';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'doc';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'xls';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'ppt';
  if (mimeType.includes('zip') || mimeType.includes('archive')) return 'zip';
  return 'file';
}

// ============================================
// COLOR UTILITIES
// ============================================

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    draft: 'bg-muted text-muted-foreground',
    pending: 'bg-warning/10 text-warning',
    confirmed: 'bg-info/10 text-info',
    active: 'bg-success/10 text-success',
    done: 'bg-success/10 text-success',
    completed: 'bg-success/10 text-success',
    paid: 'bg-success/10 text-success',
    cancelled: 'bg-destructive/10 text-destructive',
    rejected: 'bg-destructive/10 text-destructive',
    overdue: 'bg-destructive/10 text-destructive',
    expired: 'bg-destructive/10 text-destructive',
    partial: 'bg-warning/10 text-warning',
    in_progress: 'bg-info/10 text-info',
    sent: 'bg-info/10 text-info',
  };
  
  return colors[status] || 'bg-muted text-muted-foreground';
}

export function getPriorityColor(priority: string): string {
  const colors: Record<string, string> = {
    low: 'bg-muted text-muted-foreground',
    normal: 'bg-info/10 text-info',
    high: 'bg-warning/10 text-warning',
    critical: 'bg-destructive/10 text-destructive',
    urgent: 'bg-destructive/10 text-destructive',
  };
  
  return colors[priority] || 'bg-muted text-muted-foreground';
}

// ============================================
// OBJECT UTILITIES
// ============================================

export function omit<T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> {
  const result = { ...obj };
  keys.forEach(key => delete result[key]);
  return result;
}

export function pick<T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> {
  const result = {} as Pick<T, K>;
  keys.forEach(key => {
    if (key in obj) result[key] = obj[key];
  });
  return result;
}

export function isEmpty(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

// ============================================
// ARRAY UTILITIES
// ============================================

export function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce((result, item) => {
    const groupKey = String(item[key]);
    (result[groupKey] = result[groupKey] || []).push(item);
    return result;
  }, {} as Record<string, T[]>);
}

export function sortBy<T>(array: T[], key: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
}

export function uniqueBy<T>(array: T[], key: keyof T): T[] {
  const seen = new Set();
  return array.filter(item => {
    const val = item[key];
    if (seen.has(val)) return false;
    seen.add(val);
    return true;
  });
}

// ============================================
// URL UTILITIES
// ============================================

export function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, String(v)));
      } else {
        searchParams.append(key, String(value));
      }
    }
  });
  
  return searchParams.toString();
}

export function parseQueryString(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const result: Record<string, string> = {};
  
  params.forEach((value, key) => {
    result[key] = value;
  });
  
  return result;
}

// ============================================
// DEBOUNCE & THROTTLE
// ============================================

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  
  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// ============================================
// TUNISIA SPECIFIC UTILITIES
// ============================================

export const TUNISIA_GOVERNORATES = [
  { value: 'tunis', label: 'Tunis', labelAr: 'تونس' },
  { value: 'ariana', label: 'Ariana', labelAr: 'أريانة' },
  { value: 'ben_arous', label: 'Ben Arous', labelAr: 'بن عروس' },
  { value: 'manouba', label: 'Manouba', labelAr: 'منوبة' },
  { value: 'nabeul', label: 'Nabeul', labelAr: 'نابل' },
  { value: 'zaghouan', label: 'Zaghouan', labelAr: 'زغوان' },
  { value: 'bizerte', label: 'Bizerte', labelAr: 'بنزرت' },
  { value: 'beja', label: 'Béja', labelAr: 'باجة' },
  { value: 'jendouba', label: 'Jendouba', labelAr: 'جندوبة' },
  { value: 'kef', label: 'Le Kef', labelAr: 'الكاف' },
  { value: 'siliana', label: 'Siliana', labelAr: 'سليانة' },
  { value: 'sousse', label: 'Sousse', labelAr: 'سوسة' },
  { value: 'monastir', label: 'Monastir', labelAr: 'المنستير' },
  { value: 'mahdia', label: 'Mahdia', labelAr: 'المهدية' },
  { value: 'sfax', label: 'Sfax', labelAr: 'صفاقس' },
  { value: 'kairouan', label: 'Kairouan', labelAr: 'القيروان' },
  { value: 'kasserine', label: 'Kasserine', labelAr: 'القصرين' },
  { value: 'sidi_bouzid', label: 'Sidi Bouzid', labelAr: 'سيدي بوزيد' },
  { value: 'gabes', label: 'Gabès', labelAr: 'قابس' },
  { value: 'medenine', label: 'Médenine', labelAr: 'مدنين' },
  { value: 'tataouine', label: 'Tataouine', labelAr: 'تطاوين' },
  { value: 'gafsa', label: 'Gafsa', labelAr: 'قفصة' },
  { value: 'tozeur', label: 'Tozeur', labelAr: 'توزر' },
  { value: 'kebili', label: 'Kébili', labelAr: 'قبلي' },
];

export const TVA_RATES = [
  { value: 19, label: '19%', code: 'tva_19' },
  { value: 13, label: '13%', code: 'tva_13' },
  { value: 7, label: '7%', code: 'tva_7' },
  { value: 0, label: '0%', code: 'tva_0' },
];

export const TIMBRE_FISCAL = 1.000; // 1 TND

export const CNSS_RATES = {
  employee: 9.18,
  employer: 16.57,
};

export const IRPP_BRACKETS = [
  { min: 0, max: 5000, rate: 0 },
  { min: 5000.001, max: 20000, rate: 26 },
  { min: 20000.001, max: 30000, rate: 28 },
  { min: 30000.001, max: 50000, rate: 32 },
  { min: 50000.001, max: null, rate: 35 },
];

export const SMIG = {
  regime40h: 472.704,
  regime48h: 513.888,
};

// Calculate IRPP for annual income
export function calculateIRPP(annualIncome: number): number {
  let tax = 0;
  let remaining = annualIncome;
  
  for (const bracket of IRPP_BRACKETS) {
    if (remaining <= 0) break;
    
    const bracketMax = bracket.max ?? Infinity;
    const taxableInBracket = Math.min(remaining, bracketMax - bracket.min);
    
    if (taxableInBracket > 0) {
      tax += taxableInBracket * (bracket.rate / 100);
      remaining -= taxableInBracket;
    }
  }
  
  return tax;
}

// Calculate CNSS contributions
export function calculateCNSS(grossSalary: number): {
  employee: number;
  employer: number;
  total: number;
} {
  const employee = grossSalary * (CNSS_RATES.employee / 100);
  const employer = grossSalary * (CNSS_RATES.employer / 100);
  
  return {
    employee,
    employer,
    total: employee + employer,
  };
}

// Calculate net salary from gross
export function calculateNetSalary(grossSalary: number): {
  grossSalary: number;
  cnssEmployee: number;
  irpp: number;
  netSalary: number;
} {
  const cnssEmployee = grossSalary * (CNSS_RATES.employee / 100);
  const taxableIncome = (grossSalary - cnssEmployee) * 12; // Annualize
  const annualIRPP = calculateIRPP(taxableIncome);
  const monthlyIRPP = annualIRPP / 12;
  const netSalary = grossSalary - cnssEmployee - monthlyIRPP;
  
  return {
    grossSalary,
    cnssEmployee,
    irpp: monthlyIRPP,
    netSalary,
  };
}
